import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import AddToAccountModal from './AddToAccountModal';

const OPTIONS = [
  { label: 'Alpha Fleet', value: 7 },
  { label: 'Beta Fleet', value: 8 },
];

const renderModal = (props = {}) =>
  render(
    <ConfigProvider>
      <AddToAccountModal
        accountOptions={OPTIONS}
        email="abe@example.com"
        fullName="Abe Martinez"
        onChangeSelectedAccounts={jest.fn()}
        onClose={jest.fn()}
        onSearchAccounts={jest.fn()}
        onSubmit={jest.fn()}
        visible
        {...props}
      />
    </ConfigProvider>,
  );

const input = () => screen.getByLabelText('Account');

const open = async () => {
  fireEvent.focus(input());

  await waitFor(() =>
    expect(
      screen.getByRole('button', { name: /^apply$/i }),
    ).toBeInTheDocument(),
  );
};

describe('management/EditUser AddToAccountModal', () => {
  it('renders nothing when not visible', () => {
    renderModal({ visible: false });

    expect(screen.queryByText('Abe Martinez')).not.toBeInTheDocument();
  });

  it('names the target user beside their avatar', () => {
    renderModal();

    expect(screen.getByText('Abe Martinez')).toBeInTheDocument();
    expect(
      screen.getByRole('img', { name: 'Abe Martinez avatar' }),
    ).toBeInTheDocument();
  });

  describe('the Team field', () => {
    // Teams do not exist yet (PD-4213), so this is the only option there is
    // to offer — but it is a real, functioning Select, matching the mockup,
    // rather than a disabled stub.
    it('shows the single option, pre-selected', () => {
      renderModal();

      expect(screen.getByLabelText('Team')).toBeInTheDocument();
      expect(screen.getByText('All Teams')).toBeInTheDocument();
    });
  });

  describe('the Account field', () => {
    it('lists the offered accounts when opened', async () => {
      renderModal();

      await open();

      expect(screen.getByText('Alpha Fleet')).toBeInTheDocument();
      expect(screen.getByText('Beta Fleet')).toBeInTheDocument();
    });

    // Staged behind APPLY, so ticking a box alone must not reach the caller.
    it('reports nothing until APPLY', async () => {
      const onChangeSelectedAccounts = jest.fn();
      renderModal({ onChangeSelectedAccounts });

      await open();
      fireEvent.click(screen.getByText('Alpha Fleet'));

      expect(onChangeSelectedAccounts).not.toHaveBeenCalled();
    });

    it('reports every ticked account on APPLY', async () => {
      const onChangeSelectedAccounts = jest.fn();
      renderModal({ onChangeSelectedAccounts });

      await open();
      fireEvent.click(screen.getByText('Alpha Fleet'));
      fireEvent.click(screen.getByText('Beta Fleet'));
      fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));

      expect(onChangeSelectedAccounts).toHaveBeenCalledWith(['7', '8']);
    });

    it('empties the selection from CLEAR', async () => {
      const onChangeSelectedAccounts = jest.fn();
      renderModal({ onChangeSelectedAccounts, selectedAccountIds: ['7'] });

      await open();
      fireEvent.click(screen.getByRole('button', { name: /^clear$/i }));

      expect(onChangeSelectedAccounts).toHaveBeenCalledWith([]);
    });

    it('reports a search to the caller', async () => {
      const onSearchAccounts = jest.fn();
      renderModal({ onSearchAccounts });

      fireEvent.change(input(), { target: { value: 'beta' } });

      await waitFor(() =>
        expect(onSearchAccounts).toHaveBeenCalledWith('beta'),
      );
    });
  });

  describe('the primary action', () => {
    it('is disabled with no account selected', () => {
      renderModal({ selectedAccountIds: [] });

      expect(screen.getByRole('button', { name: 'Add User' })).toBeDisabled();
    });

    it('is enabled once an account is selected', () => {
      renderModal({ selectedAccountIds: ['7'] });

      expect(
        screen.getByRole('button', { name: 'Add User' }),
      ).not.toBeDisabled();
    });

    it('submits via the primary action', () => {
      const onSubmit = jest.fn();
      renderModal({ selectedAccountIds: ['7'], onSubmit });

      userEvent.click(screen.getByRole('button', { name: 'Add User' }));

      expect(onSubmit).toHaveBeenCalled();
    });

    it('shows Adding... while submitting', () => {
      renderModal({ selectedAccountIds: ['7'], isSubmitting: true });

      expect(screen.getByText('Adding...')).toBeInTheDocument();
    });
  });

  it('cancels via the secondary action', () => {
    const onClose = jest.fn();
    renderModal({ onClose });

    userEvent.click(screen.getByRole('button', { name: 'Cancel' }));

    expect(onClose).toHaveBeenCalled();
  });

  it('disables Cancel while submitting', () => {
    renderModal({ isSubmitting: true });

    expect(screen.getByRole('button', { name: 'Cancel' })).toBeDisabled();
  });

  it('shows the error inline when present', () => {
    renderModal({ error: 'Something went wrong. Please try again.' });

    expect(screen.getByRole('alert')).toHaveTextContent(
      'Something went wrong. Please try again.',
    );
  });

  it('shows no error region when there is none', () => {
    renderModal();

    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  it('titles a failure "Not added."', () => {
    renderModal({ error: 'boom' });

    expect(screen.getByRole('alert')).toHaveTextContent('Not added.');
  });
});
